import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';

/**
 * Lightbox for delivery images (proof of delivery, item photos, signature).
 *
 * Driven by a single `preview` object from the parent:
 *   { images: [{ url, label }], index: 0 }
 * Passing null closes it. Keeping the whole set (not just the clicked image)
 * means the arrows can step through the delivery's photos without closing.
 *
 * Sits above the details offcanvas (z-index 1045), hence the explicit 1060.
 */
const ImagePreviewModal = ({ preview, onClose }) => {
  const images = preview?.images || [];
  const [index, setIndex] = useState(0);

  // Re-seed whenever a new image is clicked - the modal stays mounted between
  // opens, so the index would otherwise stick at the previous one.
  useEffect(() => {
    setIndex(preview?.index ?? 0);
  }, [preview]);

  const total = images.length;
  const current = images[index] || images[0];

  const step = (offset) => {
    if (total < 2) return;
    setIndex((prev) => (prev + offset + total) % total);
  };

  return (
    <Modal
      isOpen={!!preview && !!current}
      toggle={onClose}
      size="lg"
      centered
      zIndex={1060}
    >
      <ModalHeader toggle={onClose}>
        {current?.label || 'Image'}
        {total > 1 && (
          <span className="text-muted fs-12 ms-2">
            {index + 1} / {total}
          </span>
        )}
      </ModalHeader>
      <ModalBody className="text-center position-relative">
        {total > 1 && (
          <button
            type="button"
            className="btn btn-light position-absolute top-50 start-0 translate-middle-y ms-2"
            onClick={() => step(-1)}
            title="Previous"
          >
            <i className="mdi mdi-chevron-left font-size-18"></i>
          </button>
        )}

        <img
          src={current?.url}
          alt={current?.label || 'Delivery'}
          style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
          className="rounded"
        />

        {total > 1 && (
          <button
            type="button"
            className="btn btn-light position-absolute top-50 end-0 translate-middle-y me-2"
            onClick={() => step(1)}
            title="Next"
          >
            <i className="mdi mdi-chevron-right font-size-18"></i>
          </button>
        )}

        <div className="mt-3">
          <a
            href={current?.url}
            target="_blank"
            rel="noreferrer"
            className="btn btn-sm btn-outline-secondary"
          >
            <i className="mdi mdi-open-in-new me-1"></i>
            Open original
          </a>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ImagePreviewModal;
